import Url from "url-parse";
import { isDate, isDateTime } from "./dateHelpers";
import { isNumericRange, numericRangeToString } from "./numericRangeHelper";

type jsValue = string | number | boolean | null | undefined;

interface QueryStringMap {
  [key: string]: jsValue | jsValue[] | IDate | IDateTime | INumericRange;
}

const isArray = (x: any): x is jsValue[] => Array.isArray(x);

export function getQueryStringValue(variable: string, toSearch?: string): string | null {
  const query = toSearch || window.location.search.substring(1);
  const vars = query.split("&");

  for (const kv of vars) {
    const pair = kv.split("=");
    if (decodeURIComponent(pair[0]) === variable) {
      return decodeURIComponent(pair[1]);
    }
  }

  return null;
}

export function getQueryStringValues(toSearch?: string): { [key: string]: string } {
  const vals: { [key: string]: string } = {};

  const query = toSearch || window.location.search.substring(1);
  if (query.length > 0) {
    const vars = query.split("&");

    for (const kv of vars) {
      const pair = kv.split("=");
      vals[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
    }
  }

  return vals;
}

/**
 * Format a query string. Includes preceding ?.
 *
 * Needed because querystring library doesn't handle undefined or empty arrays as our api would expect
 */
export function formatQueryString(obj: QueryStringMap): string {
  if (!obj) {
    return "";
  }

  let s = "";
  for (const key of Object.keys(obj)) {
    const propVal = obj[key];
    let val: jsValue | jsValue[];

    if (isNumericRange(propVal)) {
      val = numericRangeToString(propVal);
    }
    else if (isDateTime(propVal) || isDate(propVal)) {
      val = propVal.value;
    }
    else if (propVal !== "") {
      val = propVal;
    }

    if (val != null) {
      let escapedVal: string;
      if (isArray(val)) {
        if (val.length < 1) {
          continue;
        }

        escapedVal = val.filter(x => x != null)
          .map(x => x && encodeURIComponent(x.toString())).join(",")
      }
      else {
        escapedVal = encodeURIComponent(val.toString());
      }

      if (escapedVal) {
        if (s) {
          s += "&";
        }

        s += `${key}=${escapedVal}`;
      }
    }
  }

  return s.length > 0 ? "?" + s : "";
}

export function getCurrentUrlWithQs(obj: QueryStringMap): string {
  return `${location.protocol}//${location.host}${location.pathname}${formatQueryString(obj)}${location.hash}`;
}

/**
 * Given some text, make it look like a URL. This is not full validation!
 * Use isTypicalUrl or another method.
 * Returns null if a valid URL can't be created.
 */
export const getNormalisedUrl = (text: string | null): string | null => {
  text = (text || "").trim();

  if (!text) {
    return null;
  }

  let url = new Url(text, {});
  if (!url.protocol) {
    url.set("protocol", "http:");
    url = new Url(url.href, {}); // Reparse so that host gets set correctly
  }

  // Ignore relative paths
  // mailto links don't have host
  if (!url.host && url.protocol !== "mailto:") {
    return null;
  }

  // Ignore unknown protocols
  const knownProtocols = [ "http:", "https:", "mailto:", "ftp:" ];
  return knownProtocols.some(protocol => url.protocol === protocol)
    ? url.href
    : null;
};

/**
 * Allow a subset of URLs - protocol slash slash something dot something
 */
export const typicalUrlRegex = /((https?|ftps?):\/\/)?(\w+)(\.\w+)+\S*/i;

/**
 * Test whether the URL is a "typical" URL - e.g www.travellocal.com or http://example.com
 */
export const isTypicalUrl = (text: string | null): boolean => {
  const matches = (text || "").match(typicalUrlRegex);
  return matches?.[0] ? matches[0].length === text?.length : false;
}
